import React from 'react';
import { motion } from 'framer-motion';
import { PORTFOLIO_DATA } from '../data/portfolio';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiCpu, FiCode, FiLayers } from 'react-icons/fi';

export const Home = () => {
  const { personal, projects } = PORTFOLIO_DATA;
  const featuredProjects = projects.filter(p => p.featured);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <div className="space-y-32">
      {/* Hero Section */}
      <motion.section 
        className="pt-20 pb-16 md:pt-32 md:pb-24"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="inline-flex items-center space-x-2 bg-slate-800/50 rounded-full px-3 py-1 mb-8 border border-slate-700">
          <span className="flex h-2 w-2 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-sm text-slate-300">Scaling Solopreneur Solutions</span>
        </motion.div>
        
        <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-extrabold tracking-tight text-white mb-6">
          Hi, I'm {personal.name}.<br/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">
            {personal.role}.
          </span>
        </motion.h1>
        
        <motion.p variants={itemVariants} className="text-xl md:text-2xl text-slate-400 max-w-2xl mb-10 leading-relaxed">
          {personal.tagline}. {personal.shortBio}
        </motion.p>
        
        <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
          <Button to="/projects" variant="primary">
            View My Work <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
          </Button>
          <Button to="/about" variant="secondary">
            My Story
          </Button>
        </motion.div>
      </motion.section>

      {/* Featured Projects */}
      <section>
        <div className="flex justify-between items-end mb-10">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Featured Projects</h2>
            <p className="text-slate-400">Tools built under the Solopreneur Solutions umbrella.</p>
          </div>
          <Button to="/projects" variant="outline" className="hidden md:inline-flex">
            View All
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredProjects.map((project, index) => (
            <Card key={project.id} delay={index * 0.1} className="flex flex-col h-full">
              <div className="mb-4">
                <SafeIcon icon={index === 0 ? FiLayers : index === 1 ? FiCpu : FiCode} className="h-8 w-8 text-indigo-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-grow">
                  {project.description}
                </p>
              </div>
              <div className="mt-auto pt-4 border-t border-slate-800">
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map(tag => (
                    <Badge key={tag}>{tag}</Badge>
                  ))}
                </div>
                <Button to={`/projects`} variant="outline" className="w-full text-sm py-2">
                  Learn More
                </Button>
              </div>
            </Card>
          ))}
        </div>
        <div className="mt-8 md:hidden">
          <Button to="/projects" variant="outline" className="w-full">
            View All Projects
          </Button>
        </div>
      </section>
    </div>
  );
};